import { AndOperatorLabel } from '@modules/analysis/components/DashboardAndAnalysisFiltersPreview/AndOperatorLabel';
import { isEmptyWhereClause } from '@modules/common/utils';
import { useGetFeedWhereClause } from '@modules/feed/hooks';
import { useFeedStore } from '@modules/feed/store/feedStore';
import { FeedActionType } from '@modules/feed/store/searchActionType';
import { getCombinedFeedAndDashboardFilters } from '@modules/feed/utils';
import { FeedbackSummaryActionType } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryActionType';
import { useFeedbackSummaryStore } from '@modules/oracle/feedbackSummaryStore/feedbackSummaryStore';
import { QueryBuilder } from '@modules/queryBuilder/components/QueryBuilder';
import { trackQBEvent } from '@modules/queryBuilder/events';
import { QueryBuilderStoreProvider } from '@modules/queryBuilder/store/QueryBuilderStoreProvider';
import { QueryOnChangeProps } from '@modules/queryBuilder/types';
import { FilterAltRounded } from '@mui/icons-material';
import { Box, Button, Stack, Typography, alpha } from '@mui/material';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';

export function SearchQueryBuilderV2() {
  const whereClause = useGetFeedWhereClause({
    shouldIncludeDashboardFilters: false,
  });
  const dashboardFilters = useFeedStore(
    ({ visual }) => visual.dashboardFilters,
  );
  const dispatch = useFeedStore(({ dispatch }) => dispatch);

  const feedbackSummaryDispatch = useFeedbackSummaryStore(
    ({ dispatch }) => dispatch,
  );
  const query = useFeedStore(({ query }) => query);

  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const onExecute = ({ whereClause }: QueryOnChangeProps) => {
    dispatch({
      type: FeedActionType.SET_QUERY,
      payload: {
        whereClause,
      },
    });

    feedbackSummaryDispatch({
      type: FeedbackSummaryActionType.RESET,
    });

    trackQBEvent({
      eventName: 'Query Builder Action Event',
      action: {
        type: 'EXECUTE_QUERY',
        eql: mapToEQL({
          whereClause,
          expressionType: ExpressionType.Records,
          sourceDetailsMap: sourceDetailsMap ?? new Map(),
        }),
        display: '',
        searchMetadata: '',
        qbContext: 'Search',
      },
    });
  };

  if (
    !!dashboardFilters?.whereClause &&
    !isEmptyWhereClause(dashboardFilters.whereClause)
  ) {
    const removeDashboardWhereClauseButton = (
      <Button
        color="info"
        onClick={() => {
          const updatedDashboardFilters = {
            ...dashboardFilters,
            whereClause: undefined,
          };

          dispatch({
            type: FeedActionType.SET_VISUAL,
            payload: {
              dashboardFilters: updatedDashboardFilters,
              feedAndDashboardFilters: getCombinedFeedAndDashboardFilters({
                primaryQuery: query,
                dashboardFilters: updatedDashboardFilters,
              }),
            },
          });
        }}
      >
        Remove
      </Button>
    );

    return (
      <Stack>
        <QueryBuilderStoreProvider>
          <Box position="relative">
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{
                position: 'absolute',
                top: -10,
                left: '20px',
                zIndex: 2,
                px: 0.5,
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: '50%',
                  height: '1px',
                  backgroundColor: (theme) => theme.palette.background.default,
                  zIndex: -1,
                },
              }}
            >
              <FilterAltRounded
                color="info"
                sx={{
                  fontSize: '1rem',
                }}
              />
              <Typography variant="caption" color="text.primary">
                Dashboard Filters
              </Typography>
            </Stack>
          </Box>
          <QueryBuilder
            whereClause={dashboardFilters.whereClause ?? EmptyWhereClause}
            buttonColor="info"
            containerSxProps={{
              bgcolor: (theme) =>
                theme.palette.misc.queryBuilder.dashboardFilter.bgColor,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              borderColor: (theme) => alpha(theme.palette.info.main, 0.4),
              borderBottom: 'none',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: -1,
                left: '20px',
                right: '20px',
                height: '1px',
                backgroundColor: (theme) =>
                  theme.palette.misc.queryBuilder.dashboardFilter.bgColor,
                zIndex: 1,
              },
            }}
            secondaryActionNode={removeDashboardWhereClauseButton}
            readOnly
          />
        </QueryBuilderStoreProvider>
        <AndOperatorLabel />
        <QueryBuilder
          whereClause={whereClause ?? EmptyWhereClause}
          onExecute={onExecute}
          containerSxProps={{
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
          }}
          textFieldInputProps={{
            sx: {
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
            },
          }}
        />
      </Stack>
    );
  }

  return (
    <Box>
      <QueryBuilder
        whereClause={whereClause ?? EmptyWhereClause}
        onExecute={onExecute}
      />
    </Box>
  );
}
